/** Pure Users/roles display mapping — no I/O. D4I-B. */

import type { StatusTone } from "./overview-ops";

export type UserListRow = {
  id: string;
  email: string;
  displayName: string;
  role: string;
  mfaEnabled: boolean;
  active: boolean;
  lastLoginAt: string | null;
};

export type UserSortKey = "name" | "email" | "role" | "status" | "login";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const USERS_PAGE_NOTE =
  "کاربران و نقش‌ها حساب‌های ورود CMS را مدیریت می‌کند. فقط مالک به این صفحه دسترسی دارد. رمز، توکن MFA، یا دادهٔ نشست هرگز در فهرست یا جزئیات نمایش داده نمی‌شود.";

/** Dari labels; technical role id retained in LTR where needed. */
export const USER_ROLE_LABELS: Record<string, string> = {
  owner: "مالک",
  publisher: "ناشر",
  editor: "ویرایشگر",
  auditor: "ممیز",
};

export function userRoleLabel(role: string): string {
  return USER_ROLE_LABELS[role] ?? role;
}

export function userRoleDisplay(role: string): StatusDisplay {
  const label = userRoleLabel(role);
  if (role === "owner") {
    return { label, tone: "warning", detail: "نقش فنی: owner" };
  }
  if (role === "publisher") return { label, tone: "info", detail: `نقش فنی: ${role}` };
  if (role === "auditor") return { label, tone: "neutral", detail: `نقش فنی: ${role}` };
  return { label, tone: "info", detail: `نقش فنی: ${role}` };
}

export function userStatusDisplay(active: boolean): StatusDisplay {
  return active
    ? { label: "فعال", tone: "success" }
    : { label: "غیرفعال", tone: "neutral", detail: "ورود مسدود است" };
}

export function userMfaDisplay(enabled: boolean): StatusDisplay {
  return enabled
    ? { label: "MFA فعال", tone: "success", detail: "فقط وضعیت — بدون راز یا کد" }
    : { label: "MFA خاموش", tone: "warning" };
}

export function matchesUserQuery(row: UserListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.displayName,
    row.email,
    row.role,
    userRoleLabel(row.role),
    row.id,
    row.active ? "فعال" : "غیرفعال",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesUserFilter(row: UserListRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "active") return row.active;
  if (filter === "inactive") return !row.active;
  if (filter === "mfa_on") return row.mfaEnabled;
  if (filter === "mfa_off") return !row.mfaEnabled;
  return row.role === filter;
}

export function filterAndSortUsers(
  rows: UserListRow[],
  opts: { query: string; filter: string; sort: UserSortKey },
): UserListRow[] {
  const filtered = rows.filter(
    (r) => matchesUserQuery(r, opts.query) && matchesUserFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "email") return a.email.localeCompare(b.email, "en");
    if (opts.sort === "role") return a.role.localeCompare(b.role);
    if (opts.sort === "status") return Number(b.active) - Number(a.active);
    if (opts.sort === "login") {
      return (b.lastLoginAt ?? "").localeCompare(a.lastLoginAt ?? "");
    }
    return a.displayName.localeCompare(b.displayName, "fa");
  });
  return sorted;
}

export type RelatedUsersLink = { id: string; label: string; to: string };

export function relatedUsersLinks(): RelatedUsersLink[] {
  return [
    { id: "memberships", label: "اعضای سازمان", to: "/memberships" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
}
